import { Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useLayoutEffect, useRef, useState } from 'react';
// @ts-ignore
import { AutoSizer } from 'react-virtualized/dist/es/AutoSizer';

import { track } from '~/src/dash/analytics/analytics';
import { isIASReportType } from '~/src/dash/apiV2/reporting/reportTypes';
import { CalendarV2 } from '~/src/dash/components/calendar/CalendarV2';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacingV2 } from '~/src/dash/components/layout/common';
import {
  borderRadius,
  ButtonV2,
  Col,
  Cols,
  Div,
  fontSizingV2,
  Panel,
  Panels,
  Row,
  Rows,
  Text,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateEndDay, formatDateStartDay } from '~/src/dash/utils/date';
import {
  DateRange,
  defaultDateRanges,
  findDateRange,
  IDateRange,
} from '~/src/dash/utils/dateTimeRanges';
import { LoadingStateLoading, LoadingStateNoData } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { generateCustomDateRange } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';
import { useReportPreviewStore } from '~/src/dash/views/reporting/Preview/context';
import { LatestDataTimestamp } from '~/src/dash/views/reporting/Preview/LatestDataTimestamp';
import { ReportTable } from '~/src/dash/views/reporting/Preview/Table';

interface IReportPreview {
  copyQuery: () => Promise<any>;
  downloadReport: () => void;
  emailReport: () => void;
  shareLink: () => Promise<any>;
  toggleViewQuery: () => void;
}

export const ReportPreview = ({
  copyQuery,
  downloadReport,
  emailReport,
  shareLink,
  toggleViewQuery,
}: IReportPreview) => {
  const {
    data,
    getQueryParamValue,
    isDataOversized,
    isSuperUser,
    isReadAuthorized,
    loadState,
    onError,
    queryParams,
    rawQuery,
    reportTypeConfig,
    updateQueryParams,
  } = useReportPreviewStore();
  const { user } = useBaseStore();
  const [shareLinkFeedback, setShareLinkFeedback] = useState('');
  const [isShareLinkTooltipOpen, setIsShareLinkTooltipOpen] = useState(false);
  const [copyQueryFeedback, setCopyQueryFeedback] = useState('');
  const [isCopyQueryTooltipOpen, setIsCopyQueryTooltipOpen] = useState(false);

  // top panel ref: used for dynamically adjust the height of the table panel
  const topPanelRef = useRef<HTMLInputElement>(null);
  const [topPanelHeight, setTopPanelHeight] = useState(0);

  // check whether the report has all the valid params and if the user is authorised to read
  const isReportValid = () =>
    isReadAuthorized &&
    !isEmpty(queryParams) &&
    getQueryParamValue('metrics').length > 0 &&
    getQueryParamValue('dimensions').length > 0;

  // when user updates the date range using the date toggle, update the query
  const onDateTimeRangeTogglesUpdate = (rangeKey: DateRange) => {
    const newDateRange = findDateRange(rangeKey);
    const { startDate, endDate } = newDateRange.generateQueryDates(queryParams?.timezone);

    const maxDaysPerQuery = reportTypeConfig?.CalendarSetting?.MaxDaysPerQuery;
    if (maxDaysPerQuery > 0 && endDate.diff(startDate, 'days') + 1 > maxDaysPerQuery) {
      onError(
        'Out of range days',
        `The maximum date range that you can select for this report is: ${maxDaysPerQuery}`,
      );
      return;
    }
    updateQueryParams({
      start_date: formatDateStartDay(startDate),
      end_date: formatDateEndDay(endDate),
      report_duration_days: moment(endDate).diff(moment(startDate), 'days'),
    });
  };

  // when user updates the date range using the calendar, update the query and the date toggle
  const onCalendarUpdate = ({ newStartDate, newEndDate }: INewCalDates) => {
    if (!isSuperUser) {
      track(
        'Report Preview - Calendar',
        {
          id: user.user.ID,
          is_super_user: isSuperUser,
          load_time: moment().format('MM ddd, YYYY HH:mm:ss'),
        },
        user.user,
      );
    }
    const maxDaysPerQuery = reportTypeConfig?.CalendarSetting?.MaxDaysPerQuery;
    if (maxDaysPerQuery > 0 && newEndDate.diff(newStartDate, 'days') + 1 > maxDaysPerQuery) {
      onError(
        'Out of range days',
        `The maximum date range that you can select for this report is: ${maxDaysPerQuery}`,
      );
      return;
    }
    updateQueryParams({
      start_date: formatDateStartDay(newStartDate),
      end_date: formatDateEndDay(newEndDate),
      report_duration_days: moment(newEndDate).diff(moment(newStartDate), 'days'),
    });
  };

  // helper function to find if there is any matched preset value for the datetime toggle when
  // user updates time range using the calendar
  const findDateTimeRangeToggleValue = (
    startDate: moment.Moment,
    endDate: moment.Moment,
    timezone: string,
  ): DateRange => {
    if (!startDate || !endDate || !timezone) {
      return defaultDateRanges[0].key as DateRange;
    }
    const dateRange = defaultDateRanges.find((range) =>
      range.isWithinRange(startDate.clone().startOf('day'), endDate.clone().endOf('day'), timezone),
    );
    return (
      (dateRange?.key as DateRange) || generateCustomDateRange(endDate.diff(startDate, 'days')).key
    );
  };

  // when user clicks on "Share as link" button, copy the full url to the clipboard
  const onShareLink = async () => {
    try {
      await shareLink();
      setShareLinkFeedback('Link Copied');
      setIsShareLinkTooltipOpen(true);
      if (!isSuperUser) {
        track(
          'Report Preview - Share as link',
          {
            id: user.user.ID,
            is_super_user: isSuperUser,
            load_time: moment().format('MM ddd, YYYY HH:mm:ss'),
          },
          user.user,
        );
      }
    } catch (err) {
      setShareLinkFeedback('Failed to copy. Please check browser persmissions');
      setIsShareLinkTooltipOpen(true);
    }
  };

  // when user clicks on "Copy" button
  const onCopyQuery = async () => {
    try {
      await copyQuery();
      setCopyQueryFeedback('Query Copied');
      setIsCopyQueryTooltipOpen(true);
    } catch (err) {
      setCopyQueryFeedback('Failed to copy. Please check browser persmissions');
      setIsCopyQueryTooltipOpen(true);
    }
  };

  // dynamically update the top panel's height in order to
  // derive the height for the AutoSizer of the table
  const updateSize = () => {
    setTopPanelHeight(topPanelRef?.current?.clientHeight || 0);
  };
  useLayoutEffect(() => {
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return (
    <Panels vertical relative minWidthMain>
      <Panel
        basePadding
        largeHorizontalPadding
        paddingBottom={spacingV2.small}
        style={{
          borderBottom: `1px solid ${publicaColors.lightGray}`,
        }}
      >
        <div ref={topPanelRef}>
          <Cols wrap gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
            <Col
              displayFlex
              style={{ alignItems: 'center' }}
              marginRight={spacingV2.small}
              marginBottom={spacingV2.small}
            >
              <IconV2 name='cube' small />
            </Col>

            <Col marginRight={spacingV2.extraLarge} marginBottom={spacingV2.small}>
              <Text sizeV2 small bold>
                {queryParams?.report_name}
              </Text>
            </Col>

            <Col marginBottom={spacingV2.small}>
              <LatestDataTimestamp />
            </Col>
          </Cols>

          <Cols wrap style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Col>
              <Cols wrap gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                <Col
                  displayFlex
                  style={{ alignItems: 'center' }}
                  marginRight={spacingV2.small}
                  marginBottom={spacingV2.small}
                >
                  <IconV2 name='calendar' small />
                </Col>

                <Col marginBottom={spacingV2.small} paddingRight={spacingV2.medium}>
                  <CalendarV2
                    isDisabled={!isReadAuthorized || !reportTypeConfig?.EnableCalendarSetting}
                    calStartDate={moment(queryParams?.start_date)}
                    calEndDate={moment(queryParams?.end_date)}
                    handleApplyDatesChange={onCalendarUpdate}
                    uniqueID='previewReportCal'
                    maxDaysRange={
                      reportTypeConfig?.CalendarSetting?.MaxDaysPerQuery > 0
                        ? reportTypeConfig?.CalendarSetting?.MaxDaysPerQuery
                        : undefined
                    }
                    maxDate={moment()
                      .tz(queryParams?.timezone)
                      ?.endOf('day')}
                    minDate={
                      reportTypeConfig?.CalendarSetting?.MaxDaysLookBack > 0
                        ? moment()
                            .tz(queryParams?.timezone)
                            .subtract(reportTypeConfig?.CalendarSetting?.MaxDaysLookBack - 1, 'day')
                        : undefined
                    }
                  />
                </Col>

                <Col marginBottom={spacingV2.small}>
                  <DateTimeRangeToggles
                    backgroundColor={publicaColors.white}
                    isDisabled={!isReadAuthorized || !reportTypeConfig?.EnableCalendarSetting}
                    ranges={defaultDateRanges}
                    activeKey={findDateTimeRangeToggleValue(
                      moment.tz(queryParams?.start_date, queryParams?.timezone),
                      moment.tz(queryParams?.end_date, queryParams?.timezone),
                      queryParams?.timezone,
                    )}
                    onClick={(rangeKey: DateRange) => {
                      onDateTimeRangeTogglesUpdate(rangeKey);
                      if (!isSuperUser) {
                        track(
                          'Report Preview - Select Date Shortcut',
                          {
                            id: user.user.ID,
                            is_super_user: isSuperUser,
                            range: rangeKey,
                            load_time: moment().format('MM ddd, YYYY HH:mm:ss'),
                          },
                          user.user,
                        );
                      }
                    }}
                    checkIsRangeDisabled={(range: IDateRange) => {
                      const maxDaysPerQuery = reportTypeConfig?.CalendarSetting?.MaxDaysPerQuery;
                      if (maxDaysPerQuery <= 0) return false;
                      const dateRange = defaultDateRanges
                        .find((dateRange) => dateRange.key === range.key)
                        ?.getDateRange(queryParams?.timezone);
                      return !!(
                        dateRange && dateRange > reportTypeConfig?.CalendarSetting?.MaxDaysPerQuery
                      );
                    }}
                    style={{
                      fontSize: fontSizingV2.small,
                      padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                    }}
                  />
                </Col>
              </Cols>
            </Col>

            <Col marginBottom={spacingV2.small}>
              {loadState === LoadingStateLoading ? (
                <Cols wrap gap={spacingV2.small} style={{ alignItems: 'center' }}>
                  {[...Array(3)].map(() => {
                    return (
                      <Col>
                        <Skeleton
                          variant='rect'
                          animation='wave'
                          width={112}
                          height={32}
                          style={{ borderRadius: borderRadius.base }}
                        />
                      </Col>
                    );
                  })}
                </Cols>
              ) : (
                <Cols wrap gap={spacingV2.small} style={{ alignItems: 'center' }}>
                  {isDataOversized ? (
                    <>
                      <Col
                        noVerticalPaddingV2
                        extraSmallPaddingV2
                        style={{
                          borderRadius: borderRadius.small,
                          backgroundColor: publicaColors.lighterBlue,
                          marginRight: spacingV2.small,
                        }}
                      >
                        <Tooltip
                          title='Data looks good? Request the full report by email, you should get it shortly. Using sort on any column will refresh the preview data.'
                          leaveDelay={100}
                        >
                          <div>
                            <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                              <Col displayFlex style={{ alignItems: 'center' }}>
                                <IconV2 name='loader' small color={publicaColors.teal} />
                              </Col>
                              <Col>
                                <Text sizeV2 extraSmall>
                                  Showing first 5000 rows of data
                                </Text>
                              </Col>
                            </Cols>
                          </div>
                        </Tooltip>
                      </Col>
                      <Col>
                        <ButtonV2
                          onClick={emailReport}
                          disabled={!isReportValid()}
                          style={{
                            padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                          }}
                        >
                          <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                            <Col displayFlex style={{ alignItems: 'center' }}>
                              <IconV2 name='email' small color={publicaColors.white} />
                            </Col>
                            <Col>
                              <Text sizeV2 small bold color={publicaColors.white}>
                                Receive full report
                              </Text>
                            </Col>
                          </Cols>
                        </ButtonV2>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col
                        noVerticalPaddingV2
                        extraSmallPaddingV2
                        style={{
                          borderRadius: borderRadius.small,
                          backgroundColor: publicaColors.lighterBlue,
                          marginRight: spacingV2.small,
                        }}
                      >
                        <Tooltip title='Report is ready for instant download.' leaveDelay={100}>
                          <div>
                            <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                              <Col displayFlex style={{ alignItems: 'center' }}>
                                <IconV2 name='available' small color={publicaColors.teal} />
                              </Col>
                              <Col>
                                <Text sizeV2 extraSmall>
                                  Showing full report
                                </Text>
                              </Col>
                            </Cols>
                          </div>
                        </Tooltip>
                      </Col>
                      <Col>
                        <ButtonV2
                          onClick={downloadReport}
                          disabled={!isReportValid()}
                          style={{
                            padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                          }}
                        >
                          <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                            <Col displayFlex style={{ alignItems: 'center' }}>
                              <IconV2 name='download' small color={publicaColors.white} />
                            </Col>
                            <Col>
                              <Text sizeV2 small bold color={publicaColors.white}>
                                Download
                              </Text>
                            </Col>
                          </Cols>
                        </ButtonV2>
                      </Col>
                    </>
                  )}

                  <Col>
                    <Tooltip
                      open={isShareLinkTooltipOpen}
                      onClose={() => setIsShareLinkTooltipOpen(false)}
                      title={shareLinkFeedback}
                      leaveDelay={100}
                    >
                      <div>
                        <ButtonV2
                          clearBackground
                          onClick={onShareLink}
                          style={{
                            padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                          }}
                        >
                          <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                            <Col displayFlex style={{ alignItems: 'center' }}>
                              <IconV2 name='link' small color={publicaColors.blue} />
                            </Col>
                            <Col>
                              <Text sizeV2 small bold color={publicaColors.blue}>
                                Share as link
                              </Text>
                            </Col>
                          </Cols>
                        </ButtonV2>
                      </div>
                    </Tooltip>
                  </Col>

                  {isSuperUser && (
                    <Col>
                      <ButtonV2
                        clearBackground
                        onClick={toggleViewQuery}
                        disabled={
                          !isReportValid() || isIASReportType(Number(queryParams?.report_type))
                        }
                        style={{
                          padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                        }}
                      >
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col displayFlex style={{ alignItems: 'center' }}>
                            <IconV2
                              name={queryParams.snowflake_debug ? 'columns' : 'code'}
                              small
                              color={
                                !isReportValid() ||
                                isIASReportType(Number(queryParams?.report_type))
                                  ? publicaColors.mediumGray
                                  : publicaColors.blue
                              }
                            />
                          </Col>
                          <Col>
                            <Text
                              sizeV2
                              small
                              bold
                              color={
                                !isReportValid() ||
                                isIASReportType(Number(queryParams?.report_type))
                                  ? publicaColors.mediumGray
                                  : publicaColors.blue
                              }
                            >
                              {queryParams.snowflake_debug ? 'Table *' : 'Query *'}
                            </Text>
                          </Col>
                        </Cols>
                      </ButtonV2>
                    </Col>
                  )}
                </Cols>
              )}
            </Col>
          </Cols>
        </div>
      </Panel>

      <Panel highlighted style={{ border: 'none' }} flex noPadding>
        {isReadAuthorized ? (
          <div
            style={{
              height: `calc(100vh - ${topPanelHeight}px - ${spacingV2.base} - ${spacingV2.base})`,
              width: '100%',
              position: 'relative',
            }}
          >
            <AutoSizer>
              {({ width, height }: { width: number; height: number }) => {
                // if the report is still being fetched, in oreder to render a skeleton
                // we estimate the number of colunms and rows accordingly to the screen's size
                if (loadState === LoadingStateLoading) {
                  const maxRows = Math.floor((height - 16 * 2) / 48);
                  const maxColumns = Math.floor((width - 16 * 2) / 175);
                  const numCells = maxRows * maxColumns;
                  return (
                    <Cols
                      wrap
                      style={{
                        height: `${height}px`,
                        width: `${width}px`,
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: spacingV2.medium,
                      }}
                    >
                      {[...Array(numCells >= 1 ? numCells : 1)].map(() => {
                        return (
                          <Col
                            displayFlex
                            style={{ height: '48px', width: '175px', alignItems: 'center' }}
                          >
                            <Skeleton
                              variant='rect'
                              animation='wave'
                              width={155}
                              height={36}
                              style={{ borderRadius: '4px' }}
                            />
                          </Col>
                        );
                      })}
                    </Cols>
                  );
                }

                if (
                  !queryParams?.snowflake_debug &&
                  (loadState === LoadingStateNoData || isEmpty(data))
                ) {
                  return (
                    <Div
                      style={{
                        padding: spacingV2.base,
                        width: `${width}px`,
                        height: `${height}px`,
                      }}
                    >
                      <Text
                        sizeV2
                        large
                        bold
                        muted
                        style={{ textAlign: 'center', marginTop: spacingV2.base }}
                      >
                        No Data
                      </Text>
                    </Div>
                  );
                }

                // if (snowflake_debug === true), we display the rawQuery
                if (queryParams?.snowflake_debug) {
                  return (
                    <Div
                      style={{
                        padding: spacingV2.base,
                        width: `${width}px`,
                        height: `${height}px`,
                        overflowY: 'scroll',
                      }}
                    >
                      <Tooltip
                        open={isCopyQueryTooltipOpen}
                        onClose={() => setIsCopyQueryTooltipOpen(false)}
                        title={copyQueryFeedback}
                        leaveDelay={100}
                      >
                        <div style={{ width: 'fit-content', marginBottom: spacingV2.base }}>
                          <ButtonV2
                            backgroundColor={publicaColors.lightGray}
                            hoverColor={publicaColors.mediumGray}
                            onClick={onCopyQuery}
                            style={{
                              padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                            }}
                          >
                            <Text sizeV2 small bold muted>
                              Copy Query
                            </Text>
                          </ButtonV2>
                        </div>
                      </Tooltip>
                      <Text
                        sizeV2
                        extraSmall
                        muted
                        style={{ whiteSpace: 'pre', fontFamily: 'monospace' }}
                      >
                        {rawQuery}
                      </Text>
                    </Div>
                  );
                }

                // otherwise display the reprt data
                return <ReportTable height={height} width={width} />;
              }}
            </AutoSizer>
          </div>
        ) : (
          <Rows style={{ alignItems: 'center' }}>
            <Row displayFlex style={{ alignItems: 'center' }}>
              <Text sizeV2 large bold muted>
                Sorry you don't have the permission for this section
              </Text>
            </Row>
          </Rows>
        )}
      </Panel>
    </Panels>
  );
};
